import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api } from "../api/client.ts";
import type { Role, User } from "../api/types.ts";
import { useAuth } from "../auth/AuthProvider.tsx";
import { confirmAction, toast } from "../components/feedback.tsx";
import { Card, Drawer } from "../components/ui.tsx";

interface TeamMember extends User {
  disabledAt: string | null;
  lastLoginAt: string | null;
}

interface AuditEntry {
  id: string;
  action: string;
  actorEmail: string | null;
  resource: string | null;
  occurredAt: string;
}

export function TeamPage() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [adding, setAdding] = useState(false);

  const users = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<TeamMember[]>("/auth/users"),
  });
  const audit = useQuery({
    queryKey: ["audit"],
    queryFn: () => api.get<AuditEntry[]>("/auth/audit?limit=25"),
  });

  const [error, setError] = useState<string | null>(null);
  const refresh = () => {
    setError(null);
    void qc.invalidateQueries({ queryKey: ["users"] });
    void qc.invalidateQueries({ queryKey: ["audit"] });
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Team</h1>
        <p>Admins configure Postal, domains and senders. Members run campaigns.</p>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>People</h3>
          <button type="button" className="btn small" onClick={() => setAdding(true)}>
            + Add person
          </button>
        </div>

        {adding && (
          <Drawer title="Add a person" onClose={() => setAdding(false)}>
            <Card>
              <AddUser
                onDone={() => {
                  setAdding(false);
                  refresh();
                }}
              />
            </Card>
          </Drawer>
        )}
        {error && (
          <div className="card-body">
            <div className="error">{error}</div>
          </div>
        )}

        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Last signed in</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.data?.map((u) => (
              <PersonRow
                key={u.id}
                person={u}
                isMe={u.id === me?.id}
                onChanged={refresh}
                onError={setError}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Recent activity</h3>
          <p>Logins and configuration changes.</p>
        </div>
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Who</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {audit.data?.map((entry) => (
              <tr key={entry.id}>
                <td className="mono">{new Date(entry.occurredAt).toLocaleString()}</td>
                <td className="mono">{entry.actorEmail ?? "—"}</td>
                <td>
                  <span className={`pill ${entry.action === "login_failed" ? "bad" : "mute"}`}>
                    {entry.action}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AddUser({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("member");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.post("/auth/users", { name, email, password, role }),
    onSuccess: () => {
      onDone();
      toast("Invite created");
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <form
      className="card-body"
      style={{ background: "var(--gray-50)" }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setError(null);
        create.mutate();
      }}
    >
      {error && <div className="error">{error}</div>}
      <div className="row">
        <div className="field">
          <label htmlFor="u-name">Name</label>
          <input id="u-name" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="u-email">Email</label>
          <input
            id="u-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
      </div>
      <div className="row">
        <div className="field">
          <label htmlFor="u-password">Temporary password</label>
          <input
            id="u-password"
            type="password"
            minLength={12}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <div className="hint">At least 12 characters. They can change it once signed in.</div>
        </div>
        <div className="field">
          <label htmlFor="u-role">Role</label>
          <select id="u-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>
      <button className="btn small" type="submit" disabled={create.isPending}>
        Add person
      </button>
    </form>
  );
}

/**
 * One person, with everything an admin can do to them. The API enforces the
 * rules (no deleting or disabling yourself, never removing the last active
 * admin); the buttons only avoid offering what would certainly be refused.
 */
function PersonRow({
  person,
  isMe,
  onChanged,
  onError,
}: {
  person: TeamMember;
  isMe: boolean;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const { refresh: refreshMe } = useAuth();
  const [panel, setPanel] = useState<"edit" | "password" | null>(null);

  const act = useMutation({
    mutationFn: (action: "disable" | "enable" | "delete") =>
      action === "delete"
        ? api.del(`/auth/users/${person.id}`)
        : api.post(`/auth/users/${person.id}/${action}`),
    onSuccess: onChanged,
    onError: (err: Error) => onError(err.message),
  });

  const done = () => {
    setPanel(null);
    onChanged();
    // Your own name or email also shows in the sidebar.
    if (isMe) void refreshMe();
  };

  return (
    <>
      <tr>
        <td>
          <div style={{ fontWeight: 600 }}>
            {person.name}
            {isMe && (
              <span className="pill mute" style={{ marginLeft: 6 }}>
                you
              </span>
            )}
          </div>
          <div className="mono" style={{ color: "var(--gray-500)", fontSize: 11 }}>
            {person.email}
          </div>
        </td>
        <td>
          <span className={`pill ${person.role === "admin" ? "info" : "mute"}`}>{person.role}</span>
          {person.disabledAt && (
            <span className="pill bad" style={{ marginLeft: 6 }}>
              Disabled
            </span>
          )}
        </td>
        <td className="mono">
          {person.lastLoginAt ? new Date(person.lastLoginAt).toLocaleString() : "never"}
        </td>
        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          <button
            type="button"
            className="btn ghost small"
            onClick={() => setPanel(panel === "edit" ? null : "edit")}
          >
            Edit
          </button>
          {/* Your own password changes from your account page, which asks
              for the current one. */}
          {!isMe && (
            <>
              {" "}
              <button
                type="button"
                className="btn ghost small"
                onClick={() => setPanel(panel === "password" ? null : "password")}
              >
                Reset password
              </button>{" "}
              {person.disabledAt ? (
                <button
                  type="button"
                  className="btn ghost small"
                  disabled={act.isPending}
                  onClick={() => act.mutate("enable")}
                >
                  Enable
                </button>
              ) : (
                <button
                  type="button"
                  className="btn ghost small"
                  disabled={act.isPending}
                  onClick={async () => {
                    if (
                      await confirmAction({
                        title: `Disable ${person.name}?`,
                        message: "They are signed out straight away.",
                        confirmLabel: "Disable user",
                        destructive: true,
                      })
                    )
                      act.mutate("disable");
                  }}
                >
                  Disable
                </button>
              )}{" "}
              <button
                type="button"
                className="btn ghost small danger-text"
                disabled={act.isPending}
                onClick={async () => {
                  if (
                    await confirmAction({
                      title: `Delete ${person.name}?`,
                      message: `${person.email} is removed for good. Their activity stays in the audit log.`,
                      confirmLabel: "Delete user",
                      destructive: true,
                    })
                  )
                    act.mutate("delete");
                }}
              >
                Delete
              </button>
            </>
          )}
        </td>
      </tr>
      {panel === "edit" && (
        <tr>
          <td colSpan={4} style={{ background: "var(--gray-50)" }}>
            <EditPerson person={person} isMe={isMe} onDone={done} />
          </td>
        </tr>
      )}
      {panel === "password" && (
        <tr>
          <td colSpan={4} style={{ background: "var(--gray-50)" }}>
            <ResetPassword person={person} onDone={done} />
          </td>
        </tr>
      )}
    </>
  );
}

function EditPerson({
  person,
  isMe,
  onDone,
}: {
  person: TeamMember;
  isMe: boolean;
  onDone: () => void;
}) {
  const [name, setName] = useState(person.name);
  const [email, setEmail] = useState(person.email);
  const [role, setRole] = useState<Role>(person.role);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => api.patch(`/auth/users/${person.id}`, { name, email, role }),
    onSuccess: () => {
      onDone();
      toast("Saved");
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <form
      onSubmit={async (e: FormEvent) => {
        e.preventDefault();
        setError(null);
        if (
          isMe &&
          person.role === "admin" &&
          role === "member" &&
          !(await confirmAction({
            title: "Make yourself a member?",
            message: "You'll lose access to Setup and Team straight away.",
            confirmLabel: "Make me a member",
            destructive: true,
          }))
        )
          return;
        save.mutate();
      }}
    >
      {error && <div className="error">{error}</div>}
      <div className="row">
        <div className="field">
          <label htmlFor={`e-name-${person.id}`}>Name</label>
          <input
            id={`e-name-${person.id}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor={`e-email-${person.id}`}>Email</label>
          <input
            id={`e-email-${person.id}`}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <div className="hint">This is what they sign in with.</div>
        </div>
        <div className="field">
          <label htmlFor={`e-role-${person.id}`}>Role</label>
          <select
            id={`e-role-${person.id}`}
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>
      <button className="btn small" type="submit" disabled={save.isPending}>
        {save.isPending ? "Saving…" : "Save"}
      </button>
    </form>
  );
}

function ResetPassword({ person, onDone }: { person: TeamMember; onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reset = useMutation({
    mutationFn: () => api.post(`/auth/users/${person.id}/password`, { password }),
    onSuccess: () => {
      onDone();
      toast("Password reset");
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setError(null);
        reset.mutate();
      }}
    >
      {error && <div className="error">{error}</div>}
      <div className="field">
        <label htmlFor={`pw-${person.id}`}>New temporary password for {person.name}</label>
        <input
          id={`pw-${person.id}`}
          type="password"
          autoComplete="new-password"
          minLength={12}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <div className="hint">
          At least 12 characters. They&rsquo;re signed out everywhere; give them this password and
          they can change it from their account page.
        </div>
      </div>
      <button className="btn small" type="submit" disabled={reset.isPending}>
        {reset.isPending ? "Setting…" : "Set password"}
      </button>
    </form>
  );
}
